import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Box, Typography, useTheme, Paper } from '@mui/material';
import Chart from 'react-google-charts';
import api from '../../../utils/Url';
import { motion } from 'framer-motion';

interface Zone {
  zoneID: number;
  zoneName: string;
  zoneCode: string;
  isActive: boolean;
  sortOrder: number;
  createdDt: string;
  modifyDt: string;
  user_ID: number;
}

interface WardData {
  wardId: number;
  wardName: string;
  fileCount: number;
  zoneId: number;
  zoneName: string;
}

const DotLoader = () => {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="200px"
      position="relative"
      width={80}
      mx="auto"
    >
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          style={{
            position: 'absolute',
            left: `${i * 30}px`,
            width: 15,
            height: 15,
            borderRadius: '50%',
            backgroundColor: '#0D47A1', // Blue 900
          }}
          animate={{
            opacity: [0.2, 1, 0.2],
            scale: [0.8, 1.2, 0.8],
          }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            repeatDelay: 0,
            delay: i * 0.4,
            ease: "easeInOut"
          }}
        />
      ))}
    </Box>
  );
};

const ZoneWiseWardDashboard: React.FC = () => {
  const theme = useTheme();
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [wardData, setWardData] = useState<WardData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<any[][]>([]);

  // Fetch zones on component mount
  useEffect(() => {
    const fetchZones = async () => {
      try {
        setLoading(true);
        setInitialLoad(true);
        const response = await api.post('Zone/GetZonemaster', {
          zoneID: -1,
          user_ID: -1,
          isActive: true
        });

        if (response.data.isSuccess && response.data.data) {
          setZones(response.data.data);
          if (response.data.data.length > 0) {
            setSelectedZone(response.data.data[0]);
          }
        } else {
          setError(response.data.mesg || 'Failed to load zones');
        }
      } catch (err) {
        setError('An error occurred while fetching zones');
        console.error('Error fetching zones:', err);
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };

    fetchZones();
  }, []);

  // Prepare chart data when wardData changes
  useEffect(() => {
    if (wardData.length > 0) {
      const filteredData = wardData.filter(ward => ward.fileCount >= 0);

      const data: (string | number | object)[][] = [
        ['Ward', 'File Count', { role: 'style' }, { role: 'annotation' }]
      ];

      filteredData.forEach(ward => {
        data.push([
          ward.wardName,
          ward.fileCount,
          theme.palette.primary.main, // Bar color
          ward.fileCount.toString() // Annotation
        ]);
      });

      setChartData(data);
    } else {
      setChartData([]);
    }
  }, [wardData, theme]);

  // Fetch ward data when selected zone changes
  useEffect(() => {
    if (selectedZone) {
      const fetchWardData = async () => {
        try {
          setLoading(true);
          const response = await api.post('Dashboard/GetZoneWiseWardData', {
            zoneId: selectedZone.zoneID
          });

          if (response.data.isSuccess && response.data.data) {
            setWardData(response.data.data);
            setError(null);
          } else {
            setError(response.data.mesg || 'No ward data available for the selected zone');
            setWardData([]);
          }
        } catch (err) {
          setError('An error occurred while fetching ward data');
          console.error('Error fetching ward data:', err);
          setWardData([]);
        } finally {
          setLoading(false);
        }
      };

      fetchWardData();
    }
  }, [selectedZone]);

  const chartOptions = {
    backgroundColor: 'transparent',
    colors: [theme.palette.primary.main],
    chartArea: {
      backgroundColor: 'transparent',
      width: '85%',
      height: '75%',
      left: 80,
      top: 20,
      right: 20,
      bottom: 60
    },
    bar: {
      groupWidth: '70%'
    },
    legend: { position: 'none' },
    hAxis: {
      title: 'Ward Name',
      titleTextStyle: {
        color: theme.palette.text.primary,
        italic: false,
        fontSize: 12,
        bold: true
      },
      textStyle: {
        color: theme.palette.text.secondary,
        fontSize: 11
      },
      slantedText: true,
      slantedTextAngle: 45
    },
    vAxis: {
      title: 'File Count',
      format: '0',
      titleTextStyle: {
        color: theme.palette.text.primary,
        italic: false,
        fontSize: 12,
        bold: true
      },
      textStyle: {
        color: theme.palette.text.secondary,
        fontSize: 11
      },
      gridlines: {
        color: theme.palette.divider,
        count: -1
      },
      minorGridlines: {
        color: theme.palette.divider
      },
      minValue: 0,
      viewWindow: {
        min: 0
      },
      allowNegative: false,
      baseline: 0
    },
    animation: {
      startup: true,
      duration: 1000,
      easing: 'out'
    },
    tooltip: {
      textStyle: {
        color: theme.palette.text.primary,
        fontSize: 12
      },
      showColorCode: true
    },
    annotations: {
      textStyle: {
        color: theme.palette.getContrastText(theme.palette.primary.main),
        fontSize: 11,
        bold: true
      }
    }
  };

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {error && (
        <Typography color="error" gutterBottom sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ mb: 3, width: '100%', maxWidth: 400 }}>
        {initialLoad ? (
          <DotLoader />
        ) : (
          <Autocomplete
            options={zones}
            getOptionLabel={(option) => `${option.zoneName} (${option.zoneCode})`}
            value={selectedZone}
            onChange={(event, newValue) => {
              setSelectedZone(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Zone"
                variant="outlined"
                fullWidth
                size="small"
                InputProps={{
                  ...params.InputProps,
                  style: {
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: theme.shape.borderRadius
                  }
                }}
              />
            )}
            loading={loading}
            disabled={loading}
            sx={{
              '& .MuiAutocomplete-popper': {
                boxShadow: theme.shadows[3]
              }
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          position: 'relative',
          minHeight: '400px',
          width: '100%',
          height: '100%'
        }}
      >
        {loading ? (
          <DotLoader />
        ) : chartData.length > 1 ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              '& > div': {
                // borderRadius: theme.shape.borderRadius,
                // boxShadow: theme.shadows[1],
                // backgroundColor: theme.palette.background.paper,
              }
            }}
          >
            <Chart
              chartType="ColumnChart"
              width="100%"
              height="100%"
              data={chartData}
              options={chartOptions}
              loader={<DotLoader />}
            />
          </Box>
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.palette.background.default,
              borderRadius: theme.shape.borderRadius,
              p: 2,
              textAlign: 'center'
            }}
          >
            <Typography variant="body1" color="textSecondary">
              {selectedZone
                ? 'No ward data available for the selected zone'
                : 'Please select a zone to view data'}
            </Typography>
          </Box>
        )}
      </Box>
    </div>
  );
};

export default ZoneWiseWardDashboard;



// import React, { useState, useEffect } from 'react';
// import {
//   Autocomplete,
//   TextField,
//   Box,
//   Typography,
//   useTheme,
//   useMediaQuery,
//   Card,
//   CardContent,
//   CardHeader,
//   CircularProgress,
//   Divider,
// } from '@mui/material';
// import { BarChart } from '@mui/icons-material';
// import Chart from 'react-google-charts';
// import api from '../../../utils/Url';

// interface Zone {
//   zoneID: number;
//   zoneName: string;
//   zoneCode: string;
//   isActive: boolean;
//   sortOrder: number;
//   createdDt: string;
//   modifyDt: string;
//   user_ID: number;
// }

// interface WardData {
//   wardId: number;
//   wardName: string;
//   fileCount: number;
//   zoneId: number;
//   zoneName: string;
// }

// const ZoneWiseWardDashboard: React.FC = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
//   const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

//   const [zones, setZones] = useState<Zone[]>([]);
//   const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
//   const [wardData, setWardData] = useState<WardData[]>([]);
//   const [loading, setLoading] = useState<boolean>(true);
//   const [error, setError] = useState<string | null>(null);
//   const [chartData, setChartData] = useState<any[][]>([]);

//   useEffect(() => {
//     const fetchZones = async () => {
//       try {
//         setLoading(true);
//         const response = await api.post('Zone/GetZonemaster', {
//           zoneID: -1,
//           user_ID: -1,
//           isActive: true,
//         });

//         if (response.data.isSuccess && response.data.data) {
//           setZones(response.data.data);
//           if (response.data.data.length > 0) {
//             setSelectedZone(response.data.data[2]);
//           }
//         } else {
//           setError(response.data.mesg || 'Failed to load zones');
//         }
//       } catch (err) {
//         setError('An error occurred while fetching zones');
//         console.error(err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchZones();
//   }, []);

//   useEffect(() => {
//     if (wardData.length > 0) {
//       const data = [['Ward', 'File Count', { role: 'style' }, { role: 'annotation' }]];
//       wardData.forEach((ward: any) => {
//         data.push([
//           ward.wardName,
//           ward.fileCount,
//           theme.palette.primary.main,
//           ward.fileCount.toString(),
//         ]);
//       });
//       setChartData(data);
//     } else {
//       setChartData([]);
//     }
//   }, [wardData, theme]);

//   useEffect(() => {
//     if (selectedZone) {
//       const fetchWardData = async () => {
//         try {
//           setLoading(true);
//           const response = await api.post('Dashboard/GetZoneWiseWardData', {
//             zoneId: selectedZone.zoneID,
//           });

//           if (response.data.isSuccess && response.data.data) {
//             setWardData(response.data.data);
//           } else {
//             setError(response.data.mesg || 'Failed to load ward data');
//             setWardData([]);
//           }
//         } catch (err) {
//           setError('An error occurred while fetching ward data');
//           console.error(err);
//           setWardData([]);
//         } finally {
//           setLoading(false);
//         }
//       };

//       fetchWardData();
//     }
//   }, [selectedZone]);

//   const getChartOptions = () => ({
//     title: `Ward-wise File Count for ${selectedZone?.zoneName || ''}`,
//     titleTextStyle: {
//       color: theme.palette.text.primary,
//       fontSize: isMobile ? 14 : 20,
//       bold: true,
//     },
//     backgroundColor: theme.palette.background.paper,
//     chartArea: {
//       width: isMobile ? '80%' : '85%',
//       height: isMobile ? '60%' : '70%',
//     },
//     bar: { groupWidth: '60%' },
//     legend: { position: 'none' },
//     hAxis: {
//       title: 'Ward Name',
//       textStyle: {
//         color: theme.palette.text.secondary,
//         fontSize: isMobile ? 10 : 12,
//       },
//       slantedText: true,
//       slantedTextAngle: 45,
//     },
//     vAxis: {
//       title: 'File Count',
//       minValue: 0,
//       format: '0',
//       gridlines: { color: theme.palette.divider },
//       textStyle: {
//         color: theme.palette.text.secondary,
//       },
//     },
//     animation: {
//       startup: true,
//       duration: 1000,
//       easing: 'out',
//     },
//     annotations: {
//       textStyle: {
//         color: theme.palette.getContrastText(theme.palette.primary.main),
//         fontSize: isMobile ? 10 : 12,
//         bold: true,
//       },
//     },
//   });

//   return (
//     <Box sx={{ p: isMobile ? 1 : 3 }}>
//       <Card
//         elevation={4}
//         sx={{
//           borderRadius: 4,
//           background: `linear-gradient(to bottom, ${theme.palette.background.paper}, ${theme.palette.grey[100]})`,
//         }}
//       >
//         <CardHeader
//           avatar={<BarChart color="primary" />}
//           title={<Typography variant="h6">Zone-wise Ward Dashboard</Typography>}
//           subheader="Track file distribution across wards"
//           sx={{ pb: 0 }}
//         />
//         <Divider />

//         <CardContent sx={{ pt: 2 }}>
//           {error && (
//             <Typography color="error" sx={{ mb: 2 }}>
//               {error}
//             </Typography>
//           )}

//           <Autocomplete
//             options={zones}
//             getOptionLabel={(option) => `${option.zoneName} (${option.zoneCode})`}
//             value={selectedZone}
//             onChange={(event, newValue) => setSelectedZone(newValue)}
//             renderInput={(params) => (
//               <TextField
//                 {...params}
//                 label="Select Zone"
//                 variant="outlined"
//                 fullWidth
//                 size="medium"
//                 InputProps={{
//                   ...params.InputProps,
//                   endAdornment: (
//                     <>
//                       {loading ? <CircularProgress size={20} /> : null}
//                       {params.InputProps.endAdornment}
//                     </>
//                   ),
//                 }}
//               />
//             )}
//             sx={{
//               mb: 3,
//               backgroundColor: theme.palette.background.default,
//               borderRadius: 2,
//             }}
//           />

//           <Box
//             sx={{
//               minHeight: isMobile ? '300px' : '450px',
//               p: isMobile ? 1 : 3,
//               borderRadius: 3,
//               boxShadow: theme.shadows[1],
//               backgroundColor: theme.palette.background.default,
//               position: 'relative',
//             }}
//           >
//             {loading ? (
//               <Box
//                 sx={{
//                   height: '100%',
//                   display: 'flex',
//                   alignItems: 'center',
//                   justifyContent: 'center',
//                 }}
//               >
//                 <CircularProgress size={40} />
//               </Box>
//             ) : chartData.length > 1 ? (
//               <Chart
//                 chartType="ColumnChart"
//                 width="100%"
//                 height="100%"
//                 data={chartData}
//                 options={getChartOptions()}
//               />
//             ) : (
//               <Box
//                 sx={{
//                   height: '100%',
//                   display: 'flex',
//                   alignItems: 'center',
//                   justifyContent: 'center',
//                   textAlign: 'center',
//                 }}
//               >
//                 <Typography variant="body1">
//                   {selectedZone
//                     ? 'No ward data available for the selected zone.'
//                     : 'Please select a zone to view data.'}
//                 </Typography>
//               </Box>
//             )}
//           </Box>
//         </CardContent>
//       </Card>
//     </Box>
//   );
// };

// export default ZoneWiseWardDashboard;
